import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { users, type AppRole, type AppUser } from '../api/client';
import { useToast } from './Toast';
import { Badge, Button, ErrorState, Field, Input, Modal, Select, Spinner } from './ui';
import { formatDate } from '../lib/format';

const ROLES: AppRole[] = ['VIEW', 'EDIT', 'ADMIN'];

/**
 * Settings → Users (TASKS.md B4). ADMIN only — the api refuses the whole
 * surface for anyone else, so this panel is only ever mounted for one.
 *
 * The two destructive moves confirm first, and the api refuses the two that
 * could lock the operator out: deleting/deactivating yourself, and removing the
 * last active admin. Those answers surface as toasts, not as surprises.
 */
export function UsersPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('VIEW');
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'deactivate'; user: AppUser } | null>(null);

  const list = useQuery({
    queryKey: ['users', 200],
    queryFn: ({ signal }) => users.list({ limit: 200 }, signal),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () => users.create({ email: email.trim(), role }),
    onSuccess: (created) => {
      void invalidate();
      setEmail('');
      setRole('VIEW');
      toast(`${created.email} added as ${created.role}`, 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; patch: { role?: AppRole; active?: boolean } }) =>
      users.update(input.id, input.patch),
    onSuccess: (updated) => {
      void invalidate();
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      toast(`${updated.email} is now ${updated.role}${updated.active ? '' : ' (inactive)'}`, 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => users.remove(id),
    onSuccess: () => {
      void invalidate();
      toast('Account removed', 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="card">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="card__title" style={{ margin: 0 }}>Users</h2>
      </div>
      <p className="page-header__sub" style={{ marginTop: 0 }}>
        Who may sign in, and what they may do. VIEW reads everything, EDIT also changes data, ADMIN
        also manages accounts and reads the audit trail.
      </p>

      <form
        className="toolbar"
        style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) create.mutate();
        }}
      >
        <Field label="E-mail" htmlFor="user-email">
          <Input
            id="user-email"
            type="email"
            placeholder="name@ismart.org"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </Field>
        <Field label="Role" htmlFor="user-role">
          <Select id="user-role" value={role} onChange={(event) => setRole(event.target.value as AppRole)}>
            {ROLES.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Select>
        </Field>
        <div style={{ alignSelf: 'flex-end' }}>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!email.trim()}>
            Add user
          </Button>
        </div>
      </form>

      {list.isPending ? (
        <Spinner label="Loading users" />
      ) : list.isError ? (
        <ErrorState message={list.error.message} onRetry={() => void list.refetch()} />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">E-mail</th>
                <th scope="col">Role</th>
                <th scope="col">Active</th>
                <th scope="col">Added</th>
                <th scope="col">Last login</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((user) => (
                <tr key={user.id}>
                  <td className="cell-primary">
                    <span className="cell-clip" title={user.email}>{user.email}</span>
                  </td>
                  <td>
                    <Select
                      aria-label={`Role for ${user.email}`}
                      value={user.role}
                      onChange={(event) =>
                        update.mutate({ id: user.id, patch: { role: event.target.value as AppRole } })
                      }
                    >
                      {ROLES.map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    {user.active ? <Badge tone="ok">active</Badge> : <Badge tone="bad">inactive</Badge>}
                  </td>
                  <td className="cell-muted">{formatDate(user.createdAt)}</td>
                  <td className="cell-muted">{user.lastLoginAt ? formatDate(user.lastLoginAt) : 'never'}</td>
                  <td>
                    <div className="row-actions">
                      {user.active ? (
                        <Button size="sm" variant="secondary" onClick={() => setConfirming({ kind: 'deactivate', user })}>
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => update.mutate({ id: user.id, patch: { active: true } })}
                        >
                          Reactivate
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => setConfirming({ kind: 'delete', user })}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming?.kind === 'delete' ? 'Delete this account?' : 'Deactivate this account?'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending || update.isPending}
              onClick={() => {
                if (!confirming) return;
                if (confirming.kind === 'delete') remove.mutate(confirming.user.id);
                else update.mutate({ id: confirming.user.id, patch: { active: false } });
                setConfirming(null);
              }}
            >
              {confirming?.kind === 'delete' ? 'Delete account' : 'Deactivate account'}
            </Button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>
          {confirming?.user.email} will no longer be able to sign in
          {confirming?.kind === 'delete' ? ', and the row is gone for good' : ' until reactivated'}.
        </p>
      </Modal>
    </div>
  );
}
